import { useEffect, useState } from "react";
import { X, Loader2, CheckCircle2, Rocket, QrCode, CreditCard } from "lucide-react";
import QRCode from "qrcode";
import { api } from "../lib/api";
import { useEscapeKey } from "../lib/useEscapeKey";

/**
 * v3.9.48 (Blueprint Stage 1 / A6) — boost a listing for paid placement. Pick a
 * boost package (3/7/30 days), pay via GMPay (QRPH/card) with settlement polling,
 * reusing the same rails as SubscribeModal. Design matches the existing modals.
 */
import { formatPhp } from "../lib/money";
const peso = formatPhp;
const isRetiredBoostType = (type: string) => ["resume", "people", "person"].includes(type);

type BoostPlan = { key: string; name: string; price: number; days: number; features?: string[] };

export function BoostModal({
  open, onClose, listing, onDone,
}: {
  open: boolean;
  onClose: () => void;
  listing: { type: string; id: string; title: string } | null;
  onDone: () => void;
}) {
  const [plans, setPlans] = useState<BoostPlan[]>([]);
  const [sel, setSel] = useState<string | null>(null);
  const [method, setMethod] = useState<"qrph" | "card">("qrph");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [qrImg, setQrImg] = useState<string | null>(null);
  const [pending, setPending] = useState<null | { text: string; url?: string }>(null);
  const [done, setDone] = useState(false);
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  useEscapeKey(onClose, open && !busy && !checking);

  // Load the boost packages once when opened.
  useEffect(() => {
    if (!open || (listing && isRetiredBoostType(listing.type))) return;
    setBusy(false); setErr(null); setQrImg(null); setPending(null); setDone(false); setAvailable(null); setPlans([]); setSel(null);
    api.getPlans().then((r) => {
      setAvailable(r.paidPlansAvailable === true);
      const boosts = (r.plans || [])
        .filter((p: any) => p.kind === "one_time" && p.entitlements?.boost)
        .map((p: any) => ({ key: p.key, name: p.name, price: p.price, days: Number(p.entitlements?.days) || 0, features: p.features }))
        .sort((a: BoostPlan, b: BoostPlan) => a.days - b.days);
      setPlans(boosts);
      setSel(boosts[1]?.key || boosts[0]?.key || null); // default to the middle (7-day) tier
    }).catch(() => setErr("Could not load boost packages."));
  }, [open, listing]);

  // Poll settlement: the target listing gains an active boost once GMPay confirms.
  useEffect(() => {
    if (!open || !pending || !listing || isRetiredBoostType(listing.type)) return;
    let alive = true;
    const want = listing.type;
    const t = setInterval(async () => {
      const s = await api.myBoosts().catch(() => null);
      if (!alive || !s) return;
      if ((s.boosts || []).some((b) => b.postId === listing.id && b.postType === want)) { clearInterval(t); setDone(true); }
    }, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [open, pending, listing]);

  if (!open || !listing || isRetiredBoostType(listing.type)) return null;
  const chosen = plans.find((p) => p.key === sel) || null;

  async function pay() {
    if (!chosen) return;
    setBusy(true); setErr(null); setQrImg(null); setPending(null);
    try {
      const r = await api.boostListing({ planKey: chosen.key, postType: listing!.type, postId: listing!.id, method });
      if (r?.status === "completed") { setDone(true); return; }
      if (method === "qrph" && r?.qrRaw) {
        setQrImg(await QRCode.toDataURL(r.qrRaw, { width: 220, margin: 1, errorCorrectionLevel: "H" }));
        setPending({ text: "Scan this QRPH code to pay. Your boost activates once GMPay confirms." });
      } else if (method === "card" && r?.checkoutUrl) {
        setPending({ text: "Complete your card payment with GMPay. Your boost activates once confirmed.", url: r.checkoutUrl });
        try { window.open(r.checkoutUrl, "_blank", "noopener"); } catch { /* ignore */ }
      } else setPending({ text: "Payment started — your boost activates once GMPay confirms." });
    } catch (failure: unknown) {
      if (failure instanceof Error && "code" in failure && failure.code === "paid_plans_unavailable") setAvailable(false);
      else setErr(failure instanceof Error ? failure.message : "Could not start the payment.");
    }
    finally { setBusy(false); }
  }
  async function checkNow() {
    setChecking(true);
    try {
      const s = await api.myBoosts().catch(() => null);
      const want = listing!.type;
      if ((s?.boosts || []).some((b) => b.postId === listing!.id && b.postType === want)) setDone(true);
      else setErr("Payment not confirmed yet — give it a few seconds and check again.");
    } finally { setChecking(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className="card w-full max-w-md p-0 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-line flex items-center gap-2">
          <Rocket className="w-4 h-4 text-brand-gold" />
          <h3 className="font-bold text-navy">Boost this listing</h3>
          <button onClick={onClose} aria-label="Close" className="btn-ghost h-8 w-8 p-0 ml-auto"><X className="w-4 h-4" /></button>
        </div>

        {done ? (
          <div className="p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-brand-green mx-auto mb-2" />
            <p className="font-semibold text-navy">Boost active</p>
            <p className="text-sm text-mut mt-1">“{listing.title}” now floats to the top with a Featured badge.</p>
            <button onClick={onDone} className="btn-navy h-9 px-4 text-sm mt-4">Done</button>
          </div>
        ) : pending ? (
          <div className="p-6 text-center">
            {qrImg ? <img src={qrImg} alt="GMPay QRPH" className="w-52 h-52 mx-auto rounded-lg border border-line" />
              : <div className="w-14 h-14 rounded-full bg-brand-gold/10 grid place-items-center mx-auto mb-2"><Loader2 className="w-7 h-7 text-brand-gold animate-spin" /></div>}
            <p className="text-sm text-mut mt-3">{pending.text}</p>
            {pending.url && <a href={pending.url} target="_blank" rel="noopener noreferrer" className="text-sm text-brand-red underline">Reopen payment page</a>}
            {err && <p className="text-xs text-brand-red mt-2">{err}</p>}
            <div className="flex gap-2 justify-center mt-4">
              <button onClick={checkNow} disabled={checking} className="btn-navy h-9 px-4 text-sm disabled:opacity-60">{checking ? <Loader2 className="w-4 h-4 animate-spin" /> : "I've paid — check now"}</button>
              <button onClick={onClose} className="btn-outline h-9 px-4 text-sm">Cancel</button>
            </div>
          </div>
        ) : available === false ? (
          <div className="p-6 text-center">
            <p className="text-sm text-mut">Boost packages are coming soon.</p>
            {err && <p className="text-xs text-brand-red mt-2">{err}</p>}
            <button onClick={onClose} className="btn-outline h-9 px-4 text-sm mt-4">Continue</button>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            <p className="text-sm text-mut truncate">Boosting: <span className="font-semibold text-navy">{listing.title}</span></p>
            {plans.length === 0 ? (
              <div className="py-6 text-center text-sm text-mut"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Loading packages…</div>
            ) : (
              <div className="space-y-2">
                {plans.map((p) => (
                  <button key={p.key} onClick={() => setSel(p.key)}
                    className={`w-full flex items-center justify-between rounded-lg border p-3 text-left transition-colors ${sel === p.key ? "border-brand-gold bg-brand-gold/5" : "border-line hover:border-navy-500"}`}>
                    <span className="flex items-center gap-2">
                      <span className={`w-4 h-4 rounded-full border grid place-items-center ${sel === p.key ? "border-brand-gold" : "border-line"}`}>
                        {sel === p.key && <span className="w-2 h-2 rounded-full bg-brand-gold" />}
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-navy">{p.days} days</span>
                        <span className="block text-[11px] text-mut">Higher placement + Featured badge</span>
                      </span>
                    </span>
                    <span className="text-sm font-bold text-navy">{peso(p.price)}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center justify-center gap-2 text-xs">
              <span className="text-mut">Pay with</span>
              <button onClick={() => setMethod("qrph")} className={`chip flex items-center gap-1 ${method === "qrph" ? "bg-navy text-white" : "bg-navy-50 text-navy-600"}`}><QrCode className="w-3 h-3" />QRPH</button>
              <button onClick={() => setMethod("card")} className={`chip flex items-center gap-1 ${method === "card" ? "bg-navy text-white" : "bg-navy-50 text-navy-600"}`}><CreditCard className="w-3 h-3" />Card</button>
            </div>
            {err && <p className="text-xs text-brand-red">{err}</p>}
            <button onClick={pay} disabled={busy || !chosen} className="btn-red w-full disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}{chosen ? `Boost for ${peso(chosen.price)}` : "Boost"}</button>
            <p className="text-[11px] text-mut text-center">Secured by GMPay. Re-boosting extends your current window.</p>
          </div>
        )}
      </div>
    </div>
  );
}
